/**
 * Token API — create tokens for browser connections.
 */

import { DEFAULT_API_URL } from "./http.js";

export interface WebRTCToken {
    token: string;
    server?: string;
}

export interface TokenResponse {
    token: string;
    server: string;
    expiresIn: number;
}

export interface FetchWebRTCTokenOptions {
    agentId: string;
    apiUrl?: string;
    apiKey?: string;
}

/**
 * Token scope (CALL_LOG_SPEC.md §5).
 *
 *  · `observe`     read-only: the call log, nothing else. Agent-scoped (all
 *                  its calls) or call-scoped (one, via `callId`).
 *  · `participate` media + log — what today's webrtc/chat tokens already are.
 *  · `supervise`   observe + the control verbs of §7.
 *
 * Optional and additive: omitting it mints exactly the token this SDK has
 * always minted (§8 — `createToken("webrtc"|"chat")` keeps working).
 */
export type TokenScope = "observe" | "participate" | "supervise";

/** Extra, optional token attributes. Absent ⇒ today's behavior, byte-identical. */
export interface TokenScopeOptions {
    /** §5 scope. Absent ⇒ the server's channel default. */
    scope?: TokenScope;
    /** Narrow an `observe`/`supervise` token to a single call. */
    callId?: string;
}

export interface CreateTokenOptions extends TokenScopeOptions {
    channel: "webrtc" | "chat" | "stream";
    /**
     * One agent slug — or a non-empty list: the AGENT SET this token may see
     * (CALL_LOG_SPEC.md §5, "VISIBILITY — the agent set"). Minted per logged
     * session by YOUR backend, sealed in the token: the browser cannot widen
     * it. Stream tokens only; media channels take one agent.
     */
    agentId: string | readonly string[];
    apiKey: string;
    apiUrl?: string;
    /**
     * Sealed session metadata baked into the signed token. Trusted server-side
     * (the browser cannot forge or alter it) — surfaces as `call.metadata` for
     * tools and event handlers. Use for per-session identity (tenantId, userId,
     * role). Only honored when minting with an API key (this method). Max ~2KB.
     */
    metadata?: Record<string, unknown>;
}

export async function fetchWebRTCToken(opts: FetchWebRTCTokenOptions): Promise<WebRTCToken> {
    const apiUrl = opts.apiUrl ?? DEFAULT_API_URL;
    const headers: Record<string, string> = {};
    if (opts.apiKey) headers["Authorization"] = `Bearer ${opts.apiKey}`;

    let res: Response;
    try {
        res = await fetch(
            `${apiUrl}/webrtc/token?agent_id=${encodeURIComponent(opts.agentId)}`,
            { headers },
        );
    } catch (err) {
        throw new Error(`Network error fetching WebRTC token: ${err}`);
    }

    if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(`Failed to fetch WebRTC token: ${(data as any).detail || `HTTP ${res.status}`}`);
    }

    const data = await res.json() as Record<string, unknown>;
    if (typeof data.token !== "string") {
        throw new Error("WebRTC token response missing 'token' field");
    }

    return {
        token: data.token,
        server: (data.server as string) || undefined,
    };
}

export async function createToken(opts: CreateTokenOptions): Promise<TokenResponse> {
    const apiUrl = opts.apiUrl ?? DEFAULT_API_URL;
    const endpoints: Record<string, string> = {
        webrtc: "/webrtc/token",
        chat: "/chat/token",
        stream: "/stream/token",
    };
    const endpoint = endpoints[opts.channel] || "/webrtc/token";
    // An agent SET serializes comma-separated — the server authorizes each
    // slug through the same gate a single mint uses, then seals the set.
    const agentParam = Array.isArray(opts.agentId)
        ? opts.agentId.join(",")
        : (opts.agentId as string);
    let url = `${apiUrl}${endpoint}?agent_id=${encodeURIComponent(agentParam)}`;
    if (opts.metadata && Object.keys(opts.metadata).length > 0) {
        url += `&metadata=${encodeURIComponent(JSON.stringify(opts.metadata))}`;
    }
    // Additive (spec §5/§8): a call without scope/callId produces the exact
    // same URL as before this parameter existed — pinned by a URL snapshot
    // test in tests/token-url.test.ts.
    if (opts.scope) {
        url += `&scope=${encodeURIComponent(opts.scope)}`;
    }
    if (opts.callId) {
        url += `&call_id=${encodeURIComponent(opts.callId)}`;
    }

    let res: Response;
    try {
        res = await fetch(url, {
            headers: { Authorization: `Bearer ${opts.apiKey}` },
        });
    } catch (err) {
        throw new Error(`Network error creating ${opts.channel} token: ${err}`);
    }

    if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(
            `Failed to create ${opts.channel} token: ${(data as any).detail || `HTTP ${res.status}`}`,
        );
    }

    const data = await res.json() as Record<string, unknown>;
    if (typeof data.token !== "string") {
        throw new Error(`Token response missing 'token' field`);
    }

    return {
        token: data.token as string,
        server: (data.server as string) || apiUrl,
        expiresIn: (data.expires_in as number) || 60,
    };
}
